import type { BookingStatus, DepositOutcome } from './bookings-api';

/**
 * The i18n key of the label a booking shows, in NKS's words rather than the
 * enum's: "Awaiting deposit" only once it is on the Till; "Bike taken" when an
 * override gave its bike to another booking and nothing is chosen yet. OPEN
 * holds nothing, so until a bike is sent it reads "No bike yet".
 */
export function bookingLabelKey(booking: {
  status: BookingStatus;
  awaitingDeposit: boolean;
  bikeTaken: boolean;
}): string {
  if (booking.awaitingDeposit) return 'bookings.status.awaitingDeposit';
  if (booking.bikeTaken) return 'bookings.status.bikeTaken';
  return `bookings.status.${booking.status}`;
}

/** The i18n key for what happened to a deposit. */
export function depositOutcomeKey(outcome: DepositOutcome): string {
  return `bookings.outcome.${outcome}`;
}

/** Reserved status band from antd-theme.ts, never the brand colour. A taken bike is a warning. */
export function bookingTone(status: BookingStatus, bikeTaken = false): string | undefined {
  if (bikeTaken) return 'warning';
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'OPEN':
      return 'warning';
    case 'CANCELLED':
      return 'error';
    case 'CONVERTED':
      return 'processing';
    default:
      return undefined;
  }
}

/**
 * The smallest deposit NKS takes on a bike (Q11a). Mirrors MIN_BIKE_DEPOSIT in
 * the API, which is the one that actually enforces it — this only exists so the
 * advisor is told before they submit rather than after.
 */
export const MIN_BIKE_DEPOSIT = '100';

/** True when a decimal string is at least RM100, without parsing it as a float. */
export function meetsMinimumDeposit(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return false;
  const whole = (trimmed.split('.')[0] ?? '0').replace(/^0+/, '');
  // Digit strings of the same length compare lexicographically exactly as the
  // numbers do, which keeps this away from floats for the same reason
  // formatMoney is a string operation.
  if (whole.length !== 3) return whole.length > 3;
  return whole >= '100';
}
